<template>
  <div v-if="user" id="admin" :style="panelStyles">
    <div id="summary">
      <div class="type type-class users">
        <div class="type-ico"><i class="fa fa-fw fa-user"></i></div>
        <div class="type-in">{{ allActiveUsers }}</div>
        <div class="type-out">{{ allInactiveUsers }}</div>
      </div>
      <div class="type app-toggle" @click="toggleDashboard">
        <i class="fa toggle-state" :class="{ 'fa-chevron-up': !isOpened, 'fa-chevron-down': isOpened }"></i>
      </div>
    </div>
    <div id="panels" :class="{ offscreen: !isOpened, onscreen: isOpened }">
      <ul class="panel-nav">
        <li class="active">
          <a href="javascript:void(0);" class="panel-nav" data-target="#chat" data-toggle="tab">
            <i class="fa fa-bullhorn"></i> Announce
          </a>
        </li>
      </ul>

      <div>
        <digital-event-custom-select
          :options="messageTypes"
          name-key="title"
          value-key="value"
          class="select admin-select"
          @channel-selection="onMessageTypeChange($event)"
        >
        </digital-event-custom-select>
        <div class="timeout-select">
          <input type="radio" id="click" v-bind:value="false" v-model="autoClose" />
          <label for="click">
            <i class="fa fa-hand-pointer-o" aria-hidden="true"></i>
            Click to close
          </label>
          <input type="radio" id="auto" v-bind:value="true" v-model="autoClose" />
          <label for="auto">
            <i class="fa fa-clock-o" aria-hidden="true"></i>
            Auto close
          </label>
          <select v-model="timeout">
            <option v-for="(option, index) in timeouts" v-bind:key="'autoClose' + index + 'sec'">
              {{ option }}
            </option>
          </select>
          <span> sec</span>
        </div>
      </div>

      <div class="tab-content clearfix">
        <div id="chat">
          <div class="chat-container">
            <div class="announcement-chat chat">
              <div id="announce-new-message" class="message-new">
                <div class="input-group message">
                  <span class="input-group-btn" style="white-space: normal;">
                    <chat-emoji
                      style="margin-top: -40px;"
                      pickerWidth="#announce-new-message"
                      @emoji-picked="emojiPicked($event)"
                    ></chat-emoji>
                  </span>
                  <textarea
                    v-model="message"
                    rows="1"
                    @keyup.enter.exact.prevent="sendNewOrSavedMessage"
                    class="send-message-src form-control"
                    placeholder="Room Message"
                  ></textarea>
                  <span v-if="messageToEdit" @click="cancelEdit" class="input-group-btn" style="white-space: normal;">
                    <button class="btn btn-default" style="margin-top: -46px;">
                      <span class="glyphicon glyphicon-remove"></span>
                    </button>
                  </span>
                </div>
              </div>
              <div class="announce-new-message-btns">
                <div class="list-inline pull-right">
                  <span class="input-group-btn">
                    <button type="button" class="btn btn-light" @click="saveMessage" aria-label="Left Align">
                      <span class="fa fa-floppy-o" aria-hidden="true"></span>
                      Save
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="sendNewOrSavedMessage"
                      aria-label="Left Align"
                    >
                      <span class="glyphicon glyphicon-send" aria-hidden="true"></span> Send
                    </button>
                  </span>
                </div>
              </div>
              <div class="messages">
                <ul id="announce-messages" class="stream list-group list-group-flush" ref="messagesAnnounce">
                  <li
                    class="message-item"
                    v-for="(message, index) in draftAnnouncements"
                    :key="'announceDraftMessage-' + index"
                  >
                    <div class="message" :class="{ 'disable-message': isMessageFocused(message) }">
                      <span v-for="(line, lineNumber) of message.messageBody.split('\n')" v-bind:key="lineNumber">
                        {{ line }}<br />
                      </span>
                      <message-type :message="message"></message-type>
                    </div>
                    <div v-if="isMine(message)" class="message-saved">
                      <v-popover x-placement="top" :disabled="!!messageToEdit">
                        <button class="msg-delete" aria-label="Left Align" :disabled="messageToEdit">
                          <span
                            class="fa fa-trash"
                            aria-hidden="true"
                            :class="{ 'disable-message-btn-span': messageToEdit }"
                          ></span>
                        </button>
                        <template slot="popover">
                          <div class="confirm-popover-text">Are you sure you want to delete message?</div>
                          <div class="confirm-popover-buttons">
                            <button class="btn btn-default" v-close-popover>Cancel</button>
                            <button class="btn btn-popover-submit" v-close-popover @click="deleteMessage(message)">
                              Delete
                            </button>
                          </div>
                        </template>
                      </v-popover>
                      <button
                        class="msg-edit"
                        @click="editMessage(message)"
                        aria-label="Left Align"
                        :disabled="messageToEdit"
                      >
                        <span
                          class="fa fa-fw fa-pencil ng-scope"
                          :class="{ 'disable-message-btn-span': messageToEdit }"
                        ></span>
                      </button>
                      <v-popover x-placement="top" :disabled="!!messageToEdit">
                        <button
                          type="button"
                          class="btn btn-primary"
                          :class="{ 'disable-message-btn': messageToEdit }"
                          aria-label="Left Align"
                          :disabled="messageToEdit"
                        >
                          <span class="glyphicon glyphicon-send" aria-hidden="true"></span> Send
                        </button>
                        <template slot="popover">
                          <div class="confirm-popover-text">Are you sure you want to send this message?</div>
                          <div class="confirm-popover-buttons">
                            <button class="btn btn-default" v-close-popover>Cancel</button>
                            <button class="btn btn-popover-submit" v-close-popover @click="sendSavedMessage(message)">
                              Send
                            </button>
                          </div>
                        </template>
                      </v-popover>
                    </div>
                    <div v-else-if="!isMine(message)" class="right text-right">
                      <div class="message-data">
                        Saved
                      </div>
                      <div class="clearfix msg-time">
                        {{ formatMessageDate(message) }}
                      </div>
                      <div class="user-name">
                        {{ getUserFullName(getUserByUUID(message.userUUID)) }}
                      </div>
                    </div>
                  </li>
                  <li
                    class="message-item"
                    v-for="(message, index) in publishedAnnouncements"
                    :key="'announcePublishedMessage-' + index"
                  >
                    <div class="message">
                      <span v-for="(line, lineNumber) of message.messageBody.split('\n')" v-bind:key="lineNumber">
                        {{ line }}<br />
                      </span>
                      <message-type :message="message"></message-type>
                    </div>
                    <div class="right text-right">
                      <div class="message-data">
                        Sent
                      </div>
                      <div class="clearfix msg-time">
                        {{ formatMessageDate(message) }}
                      </div>
                      <div v-if="!isMine(message)" class="user-name">
                        {{ getUserFullName(getUserByUUID(message.userUUID)) }}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatMixin from "@/components/DigitalEvent/RoomList/Room/mixins/ChatMixin";
import DayJsMixin from "@/components/DigitalEvent/RoomList/Room/mixins/DayJsMixin.js";
import { scrollToTheTop } from "@/helpers/DOMHelper";
import { mapGetters, mapState } from "vuex";
import MessageType from "@/components/DigitalEvent/RoomList/Room/Chat/components/MessageType";
import {
  MESSAGE_STATUS,
  ANNOUNCEMENT_TYPES,
  WSS_MESSAGE_TYPES,
  TIMEOUTS,
  ANNOUNCEMENT_TYPES_DATA
} from "@/helpers/constants";
import CustomSelect from "@/components/DigitalEvent/RoomList/Room/CustomSelect";

export default {
  name: "AdminDashboard",
  mixins: [ChatMixin, DayJsMixin],
  components: {
    "digital-event-custom-select": CustomSelect,
    "chat-emoji": () => import("./Chat/components/ChatEmoji.vue"),
    "message-type": MessageType
  },
  data() {
    return {
      isOpened: false,
      action: "socket/sendAnnounce",
      messageToEdit: null,
      autoClose: true,
      timeout: 5,
      timeouts: TIMEOUTS,
      selectedMessageType: ANNOUNCEMENT_TYPES.BANNER,
      messageTypes: ANNOUNCEMENT_TYPES_DATA
    };
  },
  computed: {
    ...mapState("socket", ["announcements"]),
    ...mapGetters({
      users: "room/roomState",
      publishedAnnouncements: "socket/publishedAnnouncements",
      draftAnnouncements: "socket/draftAnnouncements",
      getUserFullName: "users/getUserFullName",
      getUserByUUID: "users/getUser"
    }),
    messages() {
      return this.$store.getters["chat/roomMessages"];
    },
    allActiveUsers() {
      return this.users.filter(u => u.isOnline).length;
    },
    allInactiveUsers() {
      return this.users.filter(u => !u.isOnline).length;
    },
    panelStyles() {
      return !this.isOpened ? { bottom: "-480px" } : { bottom: 0 };
    },
    getTimeout() {
      return this.autoClose ? parseInt(this.timeout) : 0;
    }
  },
  methods: {
    onMessageTypeChange(event) {
      this.selectedMessageType = event.value;
      event.value === ANNOUNCEMENT_TYPES.CELEBRATION
        ? (this.action = "socket/sendBanner")
        : (this.action = "socket/sendAnnounce");
    },
    toggleDashboard() {
      this.isOpened = !this.isOpened;
      scrollToTheTop("announce-messages");
    },
    getMessageType(type) {
      return this.messageTypes.find(types => types.messageType === type).value;
    },
    isMessageFocused(message) {
      return this.messageToEdit === message;
    },
    /*
     Send message
    */
    sendMessage() {
      if (!this.canSendMessage) return;

      this.$store.dispatch(this.action, { message: this.message, timeout: this.getTimeout });

      this.message = "";
    },
    /*
     Choose method to send message
    */
    sendNewOrSavedMessage() {
      if (this.messageToEdit) {
        this.messageToEdit.messageBody = this.message;
        this.sendSavedMessage(this.messageToEdit);
        this.messageToEdit = null;
      } else {
        this.sendMessage();
      }
      this.message = "";
    },

    /*
     Save message
    */
    async saveMessage() {
      if (!this.canSendMessage) {
        return;
      }

      if (this.messageToEdit) {
        await this.updateMessage(
          this.messageToEdit,
          this.messageToEdit.messageType,
          this.getTimeout,
          MESSAGE_STATUS.DRAFT,
          this.message,
          this.selectedMessageType
        );

        this.messageToEdit = null;
      } else {
        await this.$store.dispatch(this.action, {
          message: this.message,
          status: MESSAGE_STATUS.DRAFT,
          timeout: this.getTimeout
        });
      }
      this.message = "";
    },

    /*
     Send saved message
    */

    async sendSavedMessage(message) {
      await this.updateMessage(
        message,
        message.messageType,
        this.getTimeout,
        MESSAGE_STATUS.PUBLISHED,
        this.message,
        this.messageToEdit ? this.selectedMessageType : null
      );

      this.message = "";
    },

    /*
     Edit message
    */
    editMessage(message) {
      this.messageToEdit = message;
      this.message = message.messageBody;
      if (message.timeout > 0) {
        this.timeout = message.timeout;
        this.autoClose = true;
      } else {
        this.autoClose = false;
      }
    },
    /*
     Cancel message editing
    */
    cancelEdit() {
      this.messageToEdit = null;
      this.message = "";
    },

    /*
     Update message
    */
    async updateMessage(
      message,
      messageType,
      timeout,
      messageStatus = null,
      messageBody = null,
      newMessageType = null
    ) {
      let payload = {
        messageUUID: message.messageUUID,
        groupUUID: message.groupUUID,
        message: {
          messageType: messageType,
          timeout: timeout
        }
      };

      if (messageStatus) payload.message.messageStatus = messageStatus;

      if (messageBody) payload.message.messageBody = messageBody;

      if (newMessageType) {
        newMessageType === ANNOUNCEMENT_TYPES.CELEBRATION
          ? (payload.message.newMessageType = WSS_MESSAGE_TYPES.BANNER_MESSAGE)
          : (payload.message.newMessageType = WSS_MESSAGE_TYPES.ANNOUNCE_MESSAGE);
      }
      await this.$store.dispatch("chat/messageUpdate", payload);
    },
    /*
     Delete message
    */
    deleteMessage(message) {
      this.$store.dispatch("chat/messageDelete", {
        messageUUID: message.messageUUID,
        groupUUID: message.groupUUID,
        messageType: message.messageType
      });
    }
  },
  async created() {
    await this.$store.dispatch("getAnnouncements");
  }
};
</script>
